import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { doc, onSnapshot, collection, query, orderBy, limit } from 'firebase/firestore';
import Header from './Header';
import Footer from './Footer';
import ConfirmDeleteDialog from './ConfirmDeleteDialog';
import PushNotificationSettings from './PushNotificationSettings';
import PlanBillingSection from './PlanBillingSection';
import useBillingSummary from '../hooks/useBillingSummary';
import useSubscriptionFunnelEvents from '../hooks/useSubscriptionFunnelEvents';
import { usePushNotificationsEnabled } from '../hooks/useFeatureFlag';
import { auth, db, updateUserProfile, deleteUserAccount } from '../firebase';
import { disablePushNotifications } from '../utils/notificationService';
import './AccountSettingsPage.css';

// Displays account-level settings: profile (display name), email notification
// preference, transaction history, and an account deletion flow. Signed-out
// visitors see a sign-in prompt instead.
function AccountSettingsPage({ user, signIn }) {
  // One users/{uid} listener feeds both the Header's credit chip and the
  // Plan & Billing section — a separate useCredits here would be a second
  // socket on the same document, reading a subset of the same fields.
  const billing = useBillingSummary(user);
  const { credits, loading: creditsLoading } = billing;
  // This page is the Stripe Checkout success_url and the billing portal's
  // return_url, so it is where the #350 funnel closes: subscription_activated
  // and tier_upgraded / tier_downgraded (see the hook for the race it handles).
  useSubscriptionFunnelEvents(billing);
  // CreditsDisplay renders nothing on a null balance, so a signed-in account
  // whose doc has no credits map still needs the zero that useCredits used to
  // default in — otherwise the chip silently disappears on this page only.
  const headerCredits = user && !creditsLoading ? { balance: 0, ...(credits || {}) } : credits;
  // Only read the flag for signed-in users — the flags doc requires auth,
  // and the Notifications section only renders inside the signed-in branch.
  const { enabled: pushNotificationsEnabled } = usePushNotificationsEnabled({ subscribe: Boolean(user) });
  const navigate = useNavigate();

  // Absent record == NOT consented (ToS 4.3 makes ML training optional consent,
  // and consent cannot be assumed — issue #398). Starting this at `true` made
  // the toggle read ON for a user who had never agreed to anything, and the
  // first tap on it then wrote `consented: false` as if they were withdrawing.
  const [mlTrainingConsent, setMlTrainingConsent] = useState(false);
  const [tosAcceptance, setTosAcceptance] = useState(null);
  const [consentSaving, setConsentSaving] = useState(false);
  const [profileLoading, setProfileLoading] = useState(true);
  const [profileError, setProfileError] = useState(null);

  const [transactions, setTransactions] = useState([]);
  const [txnLoading, setTxnLoading] = useState(true);
  const [txnError, setTxnError] = useState(null);

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  // Single source of truth for the credit-loss warning suffix appended to
  // both the danger-zone copy and the confirm-delete dialog message.
  const creditBalance = credits?.balance || 0;
  const creditWarningSuffix = !creditsLoading && creditBalance > 0
    ? ` You have ${creditBalance} unused credit${creditBalance === 1 ? '' : 's'} that will be permanently lost.`
    : ' Any unused credits will be lost.';
  const deleteBaseMessage = 'This permanently deletes your account, all projects, and all credit history. This cannot be undone.';

  // Load profile from the user's Firestore doc. Falls back to the Firebase
  // Auth displayName / email when the doc hasn't been materialized yet.
  useEffect(() => {
    if (!user) { setProfileLoading(false); return; }
    setProfileLoading(true);
    const userRef = doc(db, 'users', user.uid);
    const unsubscribe = onSnapshot(userRef, (snap) => {
      const profile = snap.exists() ? snap.data() : {};
      // Only an explicit `consented: true` turns the toggle on. A missing
      // record, a missing field, or any other value reads as not consented.
      setMlTrainingConsent(profile.ml_training_consent?.consented === true);
      setTosAcceptance(profile.tos_acceptance || null);
      setProfileError(null);
      setProfileLoading(false);
    }, (err) => {
      console.error('Error loading profile:', err);
      setProfileError('Unable to load your profile. Please refresh to try again.');
      setProfileLoading(false);
    });
    return () => unsubscribe();
  }, [user]);

  // Transaction history subscription — mirrors the query used on
  // PurchasePage so both pages show a consistent ledger view.
  useEffect(() => {
    if (!user) { setTransactions([]); setTxnLoading(false); return; }
    const txnRef = collection(db, 'users', user.uid, 'transactions');
    const q = query(txnRef, orderBy('created_at', 'desc'), limit(5));
    const unsubscribe = onSnapshot(q, (snap) => {
      setTransactions(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setTxnError(null);
      setTxnLoading(false);
    }, (err) => {
      console.error('Error fetching transactions:', err);
      setTxnError('Unable to load transactions. Please refresh to try again.');
      setTxnLoading(false);
    });
    return () => unsubscribe();
  }, [user]);

  const handleToggleMLConsent = async () => {
    if (consentSaving) return;
    setConsentSaving(true);
    const newValue = !mlTrainingConsent;
    try {
      // No manual setMlTrainingConsent here — the onSnapshot listener
      // will pick up the Firestore write and update component state.
      await updateUserProfile({
        ml_training_consent: { consented: newValue, version: '1.0' },
      });
    } catch (err) {
      console.error('Error updating ML consent:', err);
    } finally {
      setConsentSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (deleting) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await deleteUserAccount();
      // Drop the FCM token before the auth record disappears. Best-effort.
      await disablePushNotifications().catch(() => {});
      // Auth record is gone on the server; sign the client out and return home.
      await signOut(auth).catch(() => {});
      navigate('/');
    } catch (err) {
      setDeleteError(err.message || 'Failed to delete account. Please try again.');
      setDeleting(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="account-settings-page" data-testid="account-settings-page">
      <Header user={user} signIn={signIn} credits={headerCredits} creditsLoading={creditsLoading} />
      <div className="account-settings-content">
        <h1>Account Settings</h1>

        {!user ? (
          <div className="account-signin-prompt">
            <p>Sign in to manage your account.</p>
            <button className="btn-teal" onClick={signIn}>Sign In</button>
          </div>
        ) : (
          <>
            <section className="settings-section" aria-labelledby="profile-heading">
              <h2 id="profile-heading">Profile</h2>
              {profileError && (
                <div className="settings-error" role="alert">{profileError}</div>
              )}
              {profileLoading ? (
                <p className="settings-muted">Loading profile…</p>
              ) : (
                <div className="profile-form">
                  <label className="profile-field">
                    <span>Email</span>
                    <input type="email" value={user.email || ''} disabled readOnly />
                    <span className="profile-field-help">
                      Email is managed by your sign-in provider and cannot be changed here.
                    </span>
                  </label>
                </div>
              )}
            </section>

            <PlanBillingSection
              user={user}
              entitlements={billing.entitlements}
              subscription={billing.subscription}
              credits={billing.credits}
              storageGrace={billing.storageGrace}
              loading={billing.loading}
              error={billing.error}
            />

            <section className="settings-section" aria-labelledby="tos-heading">
              <h2 id="tos-heading">Terms of Service</h2>
              {tosAcceptance ? (
                <div className="tos-acceptance-info">
                  <p className="settings-muted">
                    You accepted version <strong>{tosAcceptance.version}</strong>
                    {tosAcceptance.acceptedAt && (
                      <> on {formatDate(tosAcceptance.acceptedAt)}</>
                    )}
                    .
                  </p>
                  <p className="settings-muted" style={{ fontSize: '0.8rem' }}>
                    <Link to="/terms">View Terms of Service</Link>
                    {' '}&middot;{' '}
                    <Link to="/privacy">View Privacy Policy</Link>
                  </p>
                </div>
              ) : (
                <p className="settings-muted">
                  No acceptance on record.{' '}
                  <Link to="/terms">View Terms of Service</Link>
                </p>
              )}
            </section>

            <section className="settings-section" aria-labelledby="privacy-heading">
              <h2 id="privacy-heading">Privacy & Data</h2>
              <div className="privacy-toggle-row">
                <div className="privacy-toggle-info">
                  <strong>AI & Machine Learning Training</strong>
                  <p>
                    Allow Tweedia to use your uploaded content to train and improve
                    our AI-powered video editing tools.{' '}
                    <Link to="/privacy">Learn more</Link>
                  </p>
                </div>
                <button
                  type="button"
                  className={`toggle-btn ${mlTrainingConsent ? 'toggle-on' : 'toggle-off'}`}
                  onClick={handleToggleMLConsent}
                  disabled={consentSaving || profileLoading}
                  aria-label={mlTrainingConsent ? 'Disable ML training consent' : 'Enable ML training consent'}
                  data-testid="ml-consent-toggle"
                >
                  <span className="toggle-knob" />
                </button>
              </div>
              {/* Off is also the state of a user who has never answered the
                  consent prompt, so this caveat is worded to fit both: nothing
                  is used while this is off, and withdrawing an earlier consent
                  cannot pull content back out of a model already trained on it.
                  Held back until the profile has loaded: `mlTrainingConsent`
                  starts false, so rendering during the load would tell a user
                  who DID consent that their content is not used. */}
              {!profileLoading && !mlTrainingConsent && (
                <p className="settings-muted" style={{ fontSize: '0.8rem', marginTop: '0.5rem' }}>
                  While this is off, your content is not used for ML training. If you
                  consented previously, turning it off does not affect content already
                  used in trained models.
                </p>
              )}
            </section>

            {pushNotificationsEnabled && (
              <section className="settings-section" aria-labelledby="notifications-heading">
                <h2 id="notifications-heading">Notifications</h2>
                <PushNotificationSettings user={user} />
              </section>
            )}

            <section className="settings-section" aria-labelledby="transactions-heading">
              <h2 id="transactions-heading">Recent Transactions</h2>
              {txnError && (
                <div className="settings-error" role="alert">{txnError}</div>
              )}
              {txnLoading ? (
                <p className="settings-muted">Loading…</p>
              ) : transactions.length === 0 ? (
                <p className="settings-muted" data-testid="transactions-empty">
                  No transactions yet. Purchases and credit adjustments will show up here.
                </p>
              ) : (
                <>
                  <table className="transaction-table transaction-desktop" data-testid="transactions-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Type</th>
                        <th>Description</th>
                        <th>Amount</th>
                        <th>Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.map((txn) => (
                        <tr key={txn.id}>
                          <td>{formatDate(txn.created_at)}</td>
                          <td>{txn.type}</td>
                          <td>{txn.description}</td>
                          <td className={(txn.amount || 0) >= 0 ? 'txn-positive' : 'txn-negative'}>
                            {(txn.amount || 0) >= 0 ? '+' : ''}{txn.amount || 0}
                          </td>
                          <td>{txn.balance_after || 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="transaction-cards-preview">
                    {transactions.map((txn) => (
                      <div key={txn.id} className="transaction-card">
                        <div className="txn-card-header">
                          <span className="txn-card-type">{txn.type}</span>
                          <span className={`txn-card-amount ${(txn.amount || 0) >= 0 ? 'txn-positive' : 'txn-negative'}`}>
                            {(txn.amount || 0) >= 0 ? '+' : ''}{txn.amount || 0} credits
                          </span>
                        </div>
                        <p className="txn-card-desc">{txn.description}</p>
                        <div className="txn-card-footer">
                          <span className="txn-card-date">{formatDate(txn.created_at)}</span>
                          <span className="txn-card-balance">Balance: {txn.balance_after || 0}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="txn-view-more">
                    <Link to="/transactions" className="btn-view-more">View All Transactions</Link>
                  </div>
                </>
              )}
            </section>

            <section className="settings-section danger-zone" aria-labelledby="danger-heading">
              <h2 id="danger-heading">Delete Account</h2>
              <p>
                Permanently delete your account, all projects, and all credit history.
                This cannot be undone.{creditWarningSuffix}
              </p>
              {deleteError && (
                <div className="settings-error" role="alert">{deleteError}</div>
              )}
              <div className="danger-zone-actions">
                <button
                  type="button"
                  className="btn-red"
                  onClick={() => setShowDeleteDialog(true)}
                  disabled={deleting}
                  data-testid="delete-account-btn"
                >
                  {deleting ? 'Deleting…' : 'Delete My Account'}
                </button>
              </div>
            </section>

            {showDeleteDialog && (
              <ConfirmDeleteDialog
                title="Delete your account?"
                message={deleteBaseMessage + creditWarningSuffix}
                confirmLabel="Yes, Delete My Account"
                onCancel={() => setShowDeleteDialog(false)}
                onConfirm={() => {
                  setShowDeleteDialog(false);
                  handleConfirmDelete();
                }}
              />
            )}
          </>
        )}
      </div>
      <Footer />
    </div>
  );
}

export default AccountSettingsPage;
